import { useState } from 'react';
import { products } from '../mock';
import { Check, Mail, Phone, ArrowUpRight, Copy } from 'lucide-react';

const COPIED_RESET_MS = 2000;

const SALES_EMAIL = 'sales@northwesttitlealliance.com';
const MAIN_PHONE = { display: '(253) 540-7333', tel: 'tel:+12535407333' };
const SALES_PHONE = { display: '(253) 453-1754', tel: 'tel:+12534531754' };

const ContactIntro = () => (
  <div>
    <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-[#3E5C76]">
      Get in touch
    </div>
    <h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium tracking-[-0.02em] text-[#0B2545]">
      Ready to see what’s possible?
    </h2>
    <p className="mt-5 text-[16px] leading-relaxed text-slate-600 max-w-lg">
      Whether you’re a title officer, escrow company, or agency owner — our team will walk
      you through exactly how NTA’s products can work for your operation.
    </p>

    <ul className="mt-8 space-y-4">
      {products.map((p) => (
        <li key={p.id} className="flex items-start gap-3">
          <div className="mt-0.5 w-6 h-6 rounded-md bg-[#0B2545] flex items-center justify-center">
            <Check className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <div className="text-[14.5px] font-semibold text-[#0B2545]">
              {p.name} — {p.tagline}
            </div>
            <div className="text-[13.5px] text-slate-600">{p.bullets[0]}</div>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const CopyEmailButton = () => {
  const [copied, setCopied] = useState(false);

  const copyEmail = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(SALES_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_RESET_MS);
  };

  return (
    <button
      onClick={copyEmail}
      className="inline-flex items-center gap-1.5 rounded-full border border-white/25 hover:border-white/60 hover:bg-white/10 px-3 py-1 text-[11.5px] font-medium text-slate-200 transition-all"
      aria-label="Copy email address"
      data-testid="contact-email-copy-btn"
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5 text-emerald-400" /> Copied!
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" /> Copy
        </>
      )}
    </button>
  );
};

const EmailCard = () => (
  <a
    href={`mailto:${SALES_EMAIL}`}
    className="group block rounded-2xl bg-[#0B2545] p-8 md:p-9 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all"
    data-testid="contact-email-card"
  >
    <div className="flex items-center justify-between">
      <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
        <Mail className="w-5 h-5 text-[#8DA9C4]" />
      </div>
      <ArrowUpRight className="w-5 h-5 text-white/40 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
    </div>
    <div className="mt-6 text-[11.5px] uppercase tracking-[0.2em] font-semibold text-[#8DA9C4]">
      Email our sales team
    </div>
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <span className="font-serif text-lg sm:text-2xl font-medium text-white break-words">
        sales@<wbr />northwesttitlealliance.com
      </span>
      <CopyEmailButton />
    </div>
    <div className="mt-3 text-[13.5px] text-slate-300 leading-relaxed">
      Tell us about your operation and which product you’re interested in — we’ll reply
      with a walkthrough tailored to your desk.
    </div>
  </a>
);

const PhoneCard = ({ label, phone, note, testId }) => (
  <a
    href={phone.tel}
    className="group block rounded-2xl border border-slate-200 bg-white p-6 md:p-7 hover:shadow-lg hover:-translate-y-0.5 transition-all"
    data-testid={testId}
  >
    <div className="w-10 h-10 rounded-xl bg-[#F1F4F8] flex items-center justify-center">
      <Phone className="w-5 h-5 text-[#3E5C76]" />
    </div>
    <div className="mt-5 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3E5C76]">{label}</div>
    <div className="mt-1.5 font-serif text-xl font-semibold text-[#0B2545] group-hover:text-[#13315C] transition-colors">
      {phone.display}
    </div>
    <div className="mt-1.5 text-[12.5px] text-slate-500">{note}</div>
  </a>
);

const Contact = () => (
  <section id="contact" className="relative py-24 md:py-32 bg-gradient-to-b from-[#F1F4F8] to-white">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-2 gap-14 items-start">
        <ContactIntro />
        <div className="relative">
          <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-[#0B2545]/10 to-[#8DA9C4]/10 blur-2xl pointer-events-none" />
          <div className="relative space-y-4">
            <EmailCard />
            <div className="grid sm:grid-cols-2 gap-4">
              <PhoneCard label="Sales" phone={SALES_PHONE} note="Products, demos & new accounts" testId="contact-sales-phone" />
              <PhoneCard label="Main office" phone={MAIN_PHONE} note="Everything else" testId="contact-phone" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default Contact;
